import { ReportData, EncryptedData, Ngrams, EncryptedPayload } from "~/types/global";
import { CryptoResult } from "~/workers/crypto/interfaces";
import { REPORT_CONSTANTS } from "./constants";

export interface CombinedSearchArgs {
  [key: string]: string | number | object;
}

type KeyCounters = Record<string, number>;

type DecryptFunction = (encryptedData: EncryptedPayload, password?: string) => Promise<CryptoResult<any>>;

const searchArgs = REPORT_CONSTANTS.new_report.report_search_args

/**
 * Handles the case where a key already exists in the combinedArgs object.
 * It renames the existing key and adds the new key with a suffix.
 *
 * @param combinedArgs - The object containing all combined search arguments
 * @param keyCounters - An object tracking the count of each key
 * @param key - The key to handle
 * @param value - The value associated with the key
 */
export const handleExistingKey = (
  combinedArgs: CombinedSearchArgs,
  keyCounters: KeyCounters,
  key: string,
  value: string | number | object
): void => {
  if (keyCounters[key] === undefined) {
    // Pega a primeira chave duplicada a adiciona o sufixo
    const existingValue = combinedArgs[key];
    delete combinedArgs[key];
    combinedArgs[`${key}_1`] = existingValue;
    keyCounters[key] = 1;
  }

  keyCounters[key]++;
  combinedArgs[`${key}_${keyCounters[key]}`] = value;
};

const processReportSearchArgs = (
  report: ReportData,
  combinedArgs: CombinedSearchArgs,
  keyCounters: KeyCounters
): void => {

  const reportSearchArgs = report?.[searchArgs];
  const isNotObject = typeof reportSearchArgs !== "object";
  const isNull = reportSearchArgs === null;

  if (!reportSearchArgs || isNotObject || isNull) return;

  if ('encrypted' in reportSearchArgs && 'iv' in reportSearchArgs) {
    return;
  }

  const searchArgsObj = reportSearchArgs as Record<string, string | number | object>;

  Object.entries(searchArgsObj).forEach(([key, value]) => {
    if (combinedArgs[key] !== undefined) {
      handleExistingKey(combinedArgs, keyCounters, key, value);
    } else {
      combinedArgs[key] = value;
    }
  });
};

export const combineReportSearchArgs = (reportsDetails: ReportData[]): CombinedSearchArgs => {
  if (!reportsDetails || reportsDetails.length === 0) {
    return {};
  }

  const combinedReportSearchArgs: CombinedSearchArgs = {};
  const keyCounters: Record<string, number> = {};

  reportsDetails.forEach((report) => {
    processReportSearchArgs(report, combinedReportSearchArgs, keyCounters);
  });

  // Normaliza as chaves com sufixos (_1, _2) para arrays sem duplicatas
  const normalizedArgs: CombinedSearchArgs = {};
  const groupedKeys: Record<string, Set<string>> = {};

  Object.entries(combinedReportSearchArgs).forEach(([key, value]) => {
    const match = key.match(/^(.+)_(\d+)$/);

    if (match) {
      const baseKey = match[1];
      if (!groupedKeys[baseKey]) {
        groupedKeys[baseKey] = new Set();
      }

      if (Array.isArray(value)) {
        value.forEach(v => groupedKeys[baseKey].add(v));
      } else {
        groupedKeys[baseKey].add(value as string);
      }
    } else {
      normalizedArgs[key] = value;
    }
  });

  // Converte Sets para arrays
  Object.entries(groupedKeys).forEach(([baseKey, valueSet]) => {
    const uniqueValues = Array.from(valueSet);
    normalizedArgs[baseKey] = uniqueValues.length === 1 ? uniqueValues[0] : uniqueValues;
  });

  return normalizedArgs;
};

/**
 * Fetches and decrypts reports based on their IDs.
 */
export const fetchAndDecryptReports = async (
  reportIds: string[],
  fetchReportById: (id: string) => Promise<unknown>,
  decryptReportPayload: (report: ReportData, decryptFn: DecryptFunction) => Promise<any>,
  decryptData: DecryptFunction
): Promise<ReportData[]> => {

  const reportsDetailsPromises = reportIds.map(async (reportId) => {
    const report = await fetchReportById(reportId);

    if (!report || typeof report !== 'object') {
      throw new Error(`Failed to fetch report with ID: ${reportId}. Report data is null or invalid.`);
    }

    return decryptReportPayload(report as ReportData, decryptData);
  });

  const results = await Promise.all(reportsDetailsPromises);
  return results as ReportData[];
};